import * as PopperJS from "@popperjs/core";
import classNames from "classnames";
import { useCombobox, UseComboboxProps } from "downshift";
import React, { Children, HTMLAttributes, useCallback, useMemo, useRef, useState } from "react";
import { useElementId } from "../../hooks/use-element-id";
import { Popover } from "../popover/popover";
import { PopoverContentProps, PopoverProps } from "../popover/popover.types";
import { MenuItem } from "../types";
import { CSS_CLASSES } from "./context-menu-css-classes";
import { MenuItemRenderer } from "./context-menu-item";
import { ContextMenuItemsList, ContextMenuItemsListProps } from "./context-menu-items-list";

export interface ContextMenuAnchorProps {
  open: boolean;
  toggleButtonProps: any;
}

type ContextMenuAttributes = Pick<HTMLAttributes<HTMLElement>, "role">;

/**
 * @todo: [BDS-472] Replace the direct use of the `MenuItem` interface with a ContextMenu specific
 * derived interface. Also use this new derived interface in `ContextMenuItemsList` and `ContextMenuItem`.
 */
export interface ContextMenuProps<M extends MenuItem = MenuItem> extends ContextMenuAttributes {
  /**
   * A `JSX.Element` or a render function that returns a `JSX.Element` that will act as the **anchor element**.
   * The render function receives a `toggleButtonProps` argument that it must spread on the anchor element as props.
   * The render function also received an `open` argument that indicates the current state of the popup menu.
   * This can be used to conditionally style the anchor element.
   */
  children: ((props: ContextMenuAnchorProps) => JSX.Element) | JSX.Element;

  /**
   * A list of objects that represent each menu item in the popup menu. Objects in this list must extend the `MenuItems`
   * interface.
   *
   * @see {@link MenuItem}
   */
  menuItems: M[];

  /**
   * This prop can be used to pass a custom menu item renderer function.
   */
  menuItemRenderer?: MenuItemRenderer<M>;

  /**
   * This prop makes the popup menu trigger on hover instead of click.
   *
   * @default false
   */
  openOnHover?: boolean;

  /**
   * This prop controls the placement where the popup menu will be rendered relative to the anchor element.
   *
   * @default bottom
   */
  placement?: PopperJS.Placement;

  /**
   * Low level modifiers to be passed ot the underlying PopperJS component.
   * This is passed as it is to the corresponding prop of `Popover` component.
   */
  popoverModifiers?: PopoverProps["modifiers"];

  /**
   * This prop controls if the popup menu, that uses the `Popover` component internally, uses a React Portal or not.
   * This is passed as it is to the corresponding prop of `Popover` component.
   */
  usePortal?: PopoverProps["usePortal"];

  /**
   * A set of CSS class names to be applied to the root element of this component.
   */
  className?: string;

  /**
   * A set of CSS class names to be applied to the anchor element.
   */
  anchorClassName?: string;

  /**
   * A set of CSS class names to be applied on the popup menu element.
   */
  menuClassName?: string;

  /**
   * CSS style object to apply custom width and height styling to the popup menu.
   * This can be used to override the default width/height styling applied by this component to control something like
   * if/when a vertical scroll appears within the menu popup.
   */
  menuSizeStyle?: ContextMenuItemsListProps<M>["sizeStyle"];

  /**
   * Preserves the popup menu content element in the DOM and only visually hides it using the HTML `hidden` attribute.
   *
   * @default false
   */
  preserveDomElement?: boolean;
}

const MENU_CLOSE_DELAY_IN_MS = 300;

export const ContextMenu = <M extends MenuItem = MenuItem>({
  children,
  menuItems,
  menuItemRenderer,
  openOnHover = false,
  placement = "bottom",
  popoverModifiers,
  usePortal,
  className,
  anchorClassName: anchorClassNameProp,
  menuClassName,
  menuSizeStyle,
  preserveDomElement,
}: ContextMenuProps<M>) => {
  const [selected, setSelected] = useState(null);

  const contextMenuId = useElementId(undefined, "context-menu");
  const toggleButtonId = useMemo(() => {
    if (React.isValidElement(children) && Children.only(children) && typeof (children.props as any).id === "string") {
      return (children.props as any).id;
    }
  }, [children]);

  const useComboboxProps = useMemo(() => {
    const props: UseComboboxProps<M> = {
      id: contextMenuId,
      items: menuItems,
      circularNavigation: true,
      itemToString: (item) => item?.label ?? "",
      onSelectedItemChange: ({ selectedItem }) => {
        /**
         * Each time a new item is selected we simply want to invoke the `onClick` associated with that item.
         * Which should have been enough. However, `downshift` does not call `onSelectedItemChange` handler
         * if the same menu item is clicked again, because according to its internal state management that does
         * not amount to a `change` in the selected item.
         *
         * To overcome this, we use `downshift` in controlled mode by setting the `selectedItem` property using our own
         * state. And every time `downshift` calls this `onSelectedItemChange` handler we force set the `selectedItem`
         * to `null` thus also force setting that in `downshift` internal state. As a result, even when the same item
         * is selected a second in succession, for `downshift` it appears that it's changing to that item from `null`,
         * thus it invokes this `onSelectedItemChange` handler each time and that allows us to call the `onClick` each time.
         */
        selectedItem?.onClick?.();
        setSelected(null);
      },
      selectedItem: selected,
    };

    if (toggleButtonId) {
      props.toggleButtonId = toggleButtonId;
    }

    return props;
  }, [contextMenuId, menuItems, selected, toggleButtonId]);

  const {
    isOpen,
    getToggleButtonProps,
    getInputProps,
    getMenuProps,
    getItemProps,
    getComboboxProps,
    highlightedIndex,
    openMenu,
    closeMenu,
  } = useCombobox<M>(useComboboxProps);

  const menuCloseDelayTimerRef = useRef(null);

  const clearMenuCloseDelayTimer = useCallback(() => {
    if (menuCloseDelayTimerRef.current) {
      clearTimeout(menuCloseDelayTimerRef.current);
      menuCloseDelayTimerRef.current = null;
    }
  }, []);

  const openContextMenu = useCallback(() => {
    clearMenuCloseDelayTimer();
    if (!isOpen) {
      openMenu();
    }
  }, [clearMenuCloseDelayTimer, isOpen, openMenu]);

  const closeContextMenu = useCallback(() => {
    menuCloseDelayTimerRef.current = setTimeout(closeMenu, MENU_CLOSE_DELAY_IN_MS);
  }, [closeMenu]);

  const selectOptionsList = useMemo(
    () =>
      ({ innerRef, style }: PopoverContentProps) =>
        (
          <ContextMenuItemsList<M>
            menuItems={menuItems}
            highlightedIndex={highlightedIndex}
            getItemProps={getItemProps}
            menuItemRenderer={menuItemRenderer}
            innerRef={innerRef}
            sizeStyle={menuSizeStyle}
            style={style}
            className={menuClassName}
          />
        ),
    [getItemProps, highlightedIndex, menuClassName, menuItemRenderer, menuItems, menuSizeStyle]
  );

  const mayBeHoverInteractionHandlers = useMemo(() => {
    if (!openOnHover) {
      return undefined;
    }

    return { onMouseEnter: openContextMenu, onMouseLeave: closeContextMenu };
  }, [closeContextMenu, openContextMenu, openOnHover]);

  const contextMenuAnchorElement = useMemo<JSX.Element>(() => {
    const anchorProps = {
      ...getComboboxProps({ role: undefined }),
      ...getInputProps({ "aria-autocomplete": undefined }),
      ...getToggleButtonProps({
        "aria-haspopup": true,
        ...mayBeHoverInteractionHandlers,
      }),
      tabIndex: 0,
      role: "button",
    };

    if (typeof children === "function") {
      return children({ open: isOpen, toggleButtonProps: anchorProps });
    } else {
      return React.cloneElement(children, anchorProps);
    }
  }, [children, getComboboxProps, getInputProps, getToggleButtonProps, isOpen, mayBeHoverInteractionHandlers]);

  const overlayRootProps = useMemo(
    () =>
      getMenuProps({
        role: "menu",
        ...mayBeHoverInteractionHandlers,
      }),
    [getMenuProps, mayBeHoverInteractionHandlers]
  );

  const anchorClassName = useMemo(
    () => classNames(CSS_CLASSES.CONTEXT_MENU_ANCHOR_WRAPPER, anchorClassNameProp),
    [anchorClassNameProp]
  );

  return (
    <Popover
      placement={placement}
      modifiers={popoverModifiers}
      usePortal={usePortal}
      preserveDomElement={preserveDomElement}
      lazyPortal={false}
      className={className}
      anchorClassName={anchorClassName}
      open={isOpen}
      overlayRootProps={overlayRootProps}
      content={selectOptionsList}
    >
      {contextMenuAnchorElement}
    </Popover>
  );
};
